import { useMemo } from 'react';
import { Address } from 'vopay';
import { useTranslation } from 'react-i18next';
import { UtilButton } from 'UI';
import { useClientData } from '@/providers/ClientData.provider';
import { DetailRowsCard } from 'UI';

type USAddress = {
  Address1: string;
  Address2?: string;
  City: string;
  'Province/State': string;
  Country: string;
  'PostalCode/Zipcode': string;
};

type RecipientDetailsCardProps = {
  receiverName: string;
  accountID?: string;
  address?: Address | USAddress;
};

export default function RecipientDetailsCard({ accountID, receiverName, address }: RecipientDetailsCardProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.RecipientDetailsCard',
  });

  const {
    response: { clientAccountsEnabled },
  } = useClientData();

  const isUSAddress = (address: Address | USAddress): address is USAddress => {
    return 'Province/State' in address;
  };

  const recipientDetails = useMemo(() => {
    const details = [
      {
        label: t('recipientName', { defaultValue: 'Recipient Name' }),
        value: accountID ? (
          <UtilButton
            onClick={() => {
              if (clientAccountsEnabled) {
                window.open(`/client-accounts/${accountID}`, '_blank');
              } else {
                window.open(`/contacts/${accountID}`, '_blank');
              }
            }}
          >
            {receiverName}
          </UtilButton>
        ) : (
          receiverName
        ),
      },
    ];

    if (address) {
      const provinceState = isUSAddress(address) ? address['Province/State'] : address.Province;
      const postalCode = isUSAddress(address) ? address['PostalCode/Zipcode'] : address.PostalCode;

      details.push(
        {
          label: t('address', { defaultValue: 'Address' }),
          value: address.Address1 || address.Address2 || '-',
        },
        {
          label: t('city', { defaultValue: 'City' }),
          value: address.City || '-',
        },
        {
          label: t('provinceState', { defaultValue: 'Province / State' }),
          value: provinceState || '-',
        },
        {
          label: t('postalCodeZipcode', { defaultValue: 'PostalCode / Zipcode' }),
          value: postalCode || '-',
        },
        {
          label: t('country', { defaultValue: 'Country' }),
          value: address.Country || '-',
        },
      );
    }

    return details;
  }, [address]);

  return <DetailRowsCard title={t('title', { defaultValue: 'Recipient Information' })} details={recipientDetails} />;
}
